class SaleAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.timerId = null;
    this.resizeObserver = null;
    this.countdown = this.querySelector('[data-countdown]');
    this.staticMessage = this.querySelector('[data-static-message]');
    this.marquee = this.querySelector('[data-marquee]');
    this.track = this.querySelector('[data-marquee-track]');
    this.group = this.querySelector('[data-marquee-group]');
    this.endTimestamp = Number(this.dataset.endTimestamp);
    this.sectionWrapper = null;
    this.stickyElement = null;
    this.stickyHeader = null;
    this.onResize = this.onResize.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.updateStickyOffset = this.updateStickyOffset.bind(this);
    this.updateCountdown = this.updateCountdown.bind(this);
    this.stickyUpdateFrame = null;
  }

  connectedCallback() {
    if (!this.countdown || !this.staticMessage || !this.marquee) return;

    this.sectionWrapper = this.closest('.shopify-section');
    this.stickyElement = this.sectionWrapper || this;
    this.updateCountdown();
    if (!this.classList.contains('is-ended')) {
      this.timerId = window.setInterval(this.updateCountdown, 1000);
    }

    if (this.dataset.stickyEnabled === 'true') {
      this.stickyElement.classList.add('sale-announcement-bar-section--sticky');
      this.updateStickyOffset();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.updateStickyOffset);
    }

    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.timerId) window.clearInterval(this.timerId);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.stickyElement?.classList.remove('sale-announcement-bar-section--sticky');
    this.stickyHeader?.style.removeProperty('--sale-announcement-sticky-offset');
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.updateStickyOffset);
    if (this.stickyUpdateFrame !== null) window.cancelAnimationFrame(this.stickyUpdateFrame);
    this.timerId = null;
    this.resizeObserver = null;
    this.stickyHeader = null;
    this.stickyUpdateFrame = null;
  }

  onScroll() {
    if (this.stickyUpdateFrame !== null) return;

    this.stickyUpdateFrame = window.requestAnimationFrame(() => {
      this.stickyUpdateFrame = null;
      this.updateStickyOffset();
    });
  }

  updateStickyOffset() {
    const header = document.querySelector('.shopify-section-header');
    if (this.stickyHeader && this.stickyHeader !== header) {
      this.stickyHeader.style.removeProperty('--sale-announcement-sticky-offset');
    }
    this.stickyHeader = header;
    if (!header) return;

    const stickyTop = this.stickyElement.getBoundingClientRect().top <= 0 ? this.stickyElement.offsetHeight : 0;
    header.style.setProperty('--sale-announcement-sticky-offset', `${stickyTop}px`);
  }

  updateCountdown() {
    const remaining = this.endTimestamp - Date.now();
    if (!Number.isFinite(this.endTimestamp) || this.endTimestamp <= 0 || remaining <= 0) {
      this.showEndedState();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.setValue('[data-hours]', hours);
    this.setValue('[data-minutes]', minutes);
    this.setValue('[data-seconds]', seconds);
  }

  setValue(selector, value) {
    const slot = this.querySelector(selector);
    const current = slot?.querySelector('[data-digit-current]');
    const next = slot?.querySelector('[data-digit-next]');
    if (!slot || !current || !next) return;

    const nextValue = String(value).padStart(2, '0');
    if (current.textContent === nextValue) return;

    slot.style.width = `${Math.max(2, nextValue.length)}ch`;
    if (slot._saleRollFinish) {
      next.removeEventListener('animationend', slot._saleRollFinish);
      slot._saleRollFinish = null;
      slot.classList.remove('is-rolling');
      current.textContent = next.textContent || current.textContent;
      next.textContent = '';
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      current.textContent = nextValue;
      return;
    }

    next.textContent = nextValue;
    const finishRoll = () => {
      current.textContent = nextValue;
      next.textContent = '';
      slot.classList.remove('is-rolling');
      slot._saleRollFinish = null;
    };

    slot._saleRollFinish = finishRoll;
    next.addEventListener('animationend', finishRoll, { once: true });
    slot.classList.add('is-rolling');
  }

  showEndedState() {
    if (this.classList.contains('is-ended')) return;

    if (this.timerId) window.clearInterval(this.timerId);
    this.timerId = null;
    this.classList.add('is-ended');
    this.countdown.hidden = true;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.dataset.tickerEnabled === 'true' && !reduceMotion) {
      this.staticMessage.hidden = false;
      this.staticMessage.classList.add('visually-hidden');
      this.marquee.hidden = false;
      this.buildMarquee();
    } else {
      this.staticMessage.hidden = false;
      this.staticMessage.classList.remove('visually-hidden');
      this.marquee.hidden = true;
    }
  }

  onResize() {
    if (this.dataset.stickyEnabled === 'true') this.updateStickyOffset();

    if (this.classList.contains('is-ended') && this.dataset.tickerEnabled === 'true') {
      this.buildMarquee();
    }
  }

  buildMarquee() {
    if (!this.track || !this.group || !this.marquee) return;

    const message = this.dataset.message || '';
    const item = this.group.querySelector('[data-marquee-item]');
    if (!item || !message) return;

    this.track.querySelectorAll('[data-marquee-copy]').forEach((copy) => copy.remove());
    this.group.replaceChildren(item);
    item.textContent = message;

    const viewportWidth = this.marquee.clientWidth;
    if (!viewportWidth) return;

    while (this.group.getBoundingClientRect().width < viewportWidth * 1.25) {
      const repeat = item.cloneNode(true);
      repeat.removeAttribute('data-marquee-item');
      this.group.appendChild(repeat);
    }

    const duplicate = this.group.cloneNode(true);
    duplicate.removeAttribute('data-marquee-group');
    duplicate.setAttribute('data-marquee-copy', 'true');
    duplicate.setAttribute('aria-hidden', 'true');
    this.track.appendChild(duplicate);

    const speed = Math.max(1, Number(this.dataset.tickerSpeed) || 60);
    const distance = this.group.getBoundingClientRect().width;
    this.track.style.setProperty('--sab-marquee-duration', `${distance / speed}s`);
  }
}

if (!customElements.get('sale-announcement-bar')) {
  customElements.define('sale-announcement-bar', SaleAnnouncementBar);
}
